import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, XCircle, Clock, Bitcoin, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';

// Helper function to format dates to DD-MM-YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) return dateStr;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
};

const CryptoHealthMonitorClean = ({ loading = false }) => {
  const [cryptoHealth, setCryptoHealth] = useState({});
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const [isLoading, setIsLoading] = useState(loading);
  const navigate = useNavigate();

  const handleNavigateToIndexes = () => {
    navigate('/indexes?market=crypto&symbol=BTC&errors=errors_only');
  };

  const handleSymbolNavigate = (symbol) => {
    navigate(`/indexes?market=crypto&symbol=${symbol}&errors=errors_only`);
  };

  const periods = [
    { days: 7, label: 'Last 7 Days' },
    { days: 14, label: 'Last 14 Days' },
    { days: 30, label: 'Last 30 Days' },
  ];

  useEffect(() => {
    fetchCryptoHealth();
  }, [selectedPeriod]);

  const fetchCryptoHealth = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/analytics/crypto-health/?days=${selectedPeriod}`);
      const data = await response.json();
      setCryptoHealth(data);
    } catch (error) {
      console.error('Error fetching crypto health:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'critical': return 'bg-red-500';
      case 'missing': return 'bg-red-700';
      case 'pending': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getHealthBadge = (health) => {
    const badges = {
      healthy: 'health-status-ok',
      warning: 'health-status-warning',
      critical: 'health-status-critical',
    };
    
    const icons = {
      healthy: '✅ Healthy',
      warning: '⚠️ Warning',
      critical: '❌ Critical',
    };
    
    return (
      <span className={badges[health] || badges.warning}>
        {icons[health] || '❓ Unknown'}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="dashboard-card">
        <div className="flex items-center space-x-2 mb-6">
          <div className="w-5 h-5 bg-dark-700 rounded animate-pulse"></div>
          <div className="h-6 bg-dark-700 rounded w-64 animate-pulse"></div>
        </div>
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="h-32 bg-dark-700 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-card">
      {/* Header with Tabs */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Bitcoin className="w-5 h-5 text-orange-400" />
          <h3 className="text-xl font-semibold text-white">Crypto Smart Index Health</h3>
          <button
            onClick={handleNavigateToIndexes}
            className="ml-2 p-1 text-dark-400 hover:text-primary-400 transition-colors"
            title="View detailed crypto indexes"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
        <div className="flex space-x-2">
          {periods.map((period) => (
            <button
              key={period.days}
              onClick={() => setSelectedPeriod(period.days)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedPeriod === period.days ? 'tab-active' : 'tab-inactive'
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      {/* Status Legend */}
      <div className="mb-6 p-4 bg-gradient-to-r from-dark-800 to-dark-900 rounded-lg border border-dark-600">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-white">Status Guide</h4>
          <span className="text-xs text-dark-400">Monitoring required columns for smart_index_st calculation</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded"></div>
            <span className="text-sm text-dark-200">Healthy - All data present</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-yellow-500 rounded"></div>
            <span className="text-sm text-dark-200">Warning - Minor issues</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-500 rounded"></div>
            <span className="text-sm text-dark-200">Critical - Major issues</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-700 rounded"></div>
            <span className="text-sm text-dark-200">Missing - Should exist</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-500 rounded"></div>
            <span className="text-sm text-dark-200">Pending - Not due yet</span>
          </div>
        </div>
      </div>

      {/* Crypto Cards - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Object.entries(cryptoHealth.crypto_health || {}).map(([symbol, data]) => {
          const hasCriticalIssues = data.overall_health === 'critical' || data.critical_days > 0 || data.missing_days > 0;
          
          return (
            <div key={symbol} className={`border rounded-lg p-5 bg-gradient-to-br from-dark-800/50 to-dark-900/50 transition-all hover:from-dark-700/50 hover:to-dark-800/50 ${
              hasCriticalIssues ? 'border-red-500/50 shadow-lg shadow-red-500/10' : 'border-dark-700'
            }`}>
              
              {/* Critical Issues Alert */}
              {hasCriticalIssues && (
                <div className="mb-4 p-3 bg-red-900/30 border border-red-800/50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <span className="text-red-400 font-semibold text-sm">
                      CRITICAL DATA ISSUES DETECTED
                    </span>
                  </div>
                  <p className="text-red-300 text-xs mt-1">
                    {data.critical_days > 0 && `${data.critical_days} critical days, `}
                    {data.missing_days > 0 && `${data.missing_days} missing days`}
                  </p>
                </div>
              )}
              
              {/* Card Header - Clickable */}
              <div 
                className="flex items-center justify-between mb-4 cursor-pointer hover:bg-dark-700/30 rounded-lg p-2 -m-2 transition-colors"
                onClick={() => handleSymbolNavigate(symbol)}
                title={`View detailed ${symbol} data quality records`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white transition-transform hover:scale-105 ${
                    symbol === 'BTC' ? 'bg-gradient-to-br from-orange-500 to-yellow-600' : 
                    'bg-gradient-to-br from-blue-500 to-purple-600'
                  } ${hasCriticalIssues ? 'ring-2 ring-red-500/50' : ''}`}>
                    {symbol}
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white hover:text-primary-400 transition-colors">
                      {data.symbol_name}
                    </h4>
                    <p className="text-dark-400 text-sm">
                      Updated: {new Date(data.latest_update).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {getHealthBadge(data.overall_health)}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-5 gap-2 mb-4">
                <div className="text-center bg-dark-800 rounded-lg p-2">
                  <p className="text-lg font-bold text-green-400">{data.healthy_days}</p>
                  <p className="text-xs text-dark-400">Healthy</p>
                </div>
                <div className="text-center bg-dark-800 rounded-lg p-2">
                  <p className="text-lg font-bold text-yellow-400">{data.warning_days}</p>
                  <p className="text-xs text-dark-400">Warning</p>
                </div>
                <div className={`text-center rounded-lg p-2 ${data.critical_days > 0 ? 'bg-red-900/40 ring-1 ring-red-500' : 'bg-dark-800'}`}>
                  <p className={`text-lg font-bold ${data.critical_days > 0 ? 'text-red-300 text-xl' : 'text-red-400'}`}>
                    {data.critical_days}
                  </p>
                  <p className="text-xs text-dark-400">Critical</p>
                </div>
                <div className={`text-center rounded-lg p-2 ${data.missing_days > 0 ? 'bg-red-900/40 ring-1 ring-red-500' : 'bg-dark-800'}`}>
                  <p className={`text-lg font-bold ${data.missing_days > 0 ? 'text-red-300 text-xl' : 'text-red-600'}`}>
                    {data.missing_days}
                  </p>
                  <p className="text-xs text-dark-400">Missing</p>
                </div>
                <div className="text-center bg-dark-800 rounded-lg p-2">
                  <p className="text-lg font-bold text-blue-400">{data.pending_days || 0}</p>
                  <p className="text-xs text-dark-400">Pending</p>
                </div>
              </div>

              {/* Daily Timeline */}
              <div className="bg-dark-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-white">Daily Status Timeline</p>
                  <span className="text-xs text-dark-400">{selectedPeriod} days</span>
                </div>
                
                <div className="flex justify-center mb-3">
                  <div className="flex gap-1 flex-wrap max-w-full">
                    {data.daily_status?.slice(-selectedPeriod).map((day, index) => (
                      <div
                        key={index}
                        className={`w-3 h-6 rounded-sm ${getStatusColor(day.status)} flex-shrink-0`}
                        title={`${formatDate(day.date)}: ${day.status}${day.issues.length > 0 ? '\nIssues: ' + day.issues.join(', ') : ''}`}
                      ></div>
                    ))}
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-xs text-dark-400">
                  <span>{selectedPeriod} days ago</span>
                  <span>Today</span>
                </div>
              </div>

              {/* Issues Alert */}
              {Object.keys(data.null_columns || {}).length > 0 && (
                <div className="mt-4 bg-red-900/20 border border-red-800 rounded-lg p-4">
                  <p className="text-sm font-medium text-red-400 mb-3 flex items-center">
                    <AlertTriangle size={16} className="mr-2" />
                    Data Issues Detected
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {Object.entries(data.null_columns).slice(0, 4).map(([column, count]) => (
                      <div key={column} className="flex justify-between bg-red-900/10 rounded px-2 py-1">
                        <span className="text-dark-200">{column.toUpperCase()}</span>
                        <span className="text-red-400 font-semibold">{count} missing</span>
                      </div>
                    ))}
                  </div>
                  {Object.keys(data.null_columns || {}).length > 4 && (
                    <p className="text-xs text-dark-400 mt-2">
                      +{Object.keys(data.null_columns).length - 4} more issues
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CryptoHealthMonitorClean;